import { Fragment, useMemo, useState, type CSSProperties, type MutableRefObject } from 'react';
import { builderPageTypeCatalog, builderSectionCatalog, builderThemePresetCatalog, summarizeBuilderSection } from '@shared/builder/document';
import { getBuilderThemeCssVariables } from '@shared/builder/presets';
import type {
  AboutSection,
  AssetRecord,
  BuilderAssetReference,
  BuilderLinkField,
  BuilderMenuLocation,
  BuilderPageStatus,
  BuilderPageType,
  BuilderPreviewDevice,
  BuilderSection,
  BuilderSectionType,
  BuilderThemePresetId,
  ProjectDetailRecord
} from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { EditorTabs, EmptyState, IconButton, SelectField, StatusBadge, TextAreaField, TextField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { getAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import { useUiStore } from '@renderer/stores/ui-store';
import { updateProject } from '../projects-repository';
import { ProjectBuilderAiBubble } from './project-builder-ai-bubble';
import { useProjectBuilderStore } from './project-builder-store';
import { useProjectBuilderController } from './use-project-builder-controller';

const builderDragMimeType = 'application/x-alttvision-builder-section';
type Translate = ReturnType<typeof useI18n>['t'];
type BuilderWorkspaceTab = 'content' | 'pages' | 'seo' | 'theme';

function createLocalId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function readDraggedSectionId(event: React.DragEvent<HTMLElement>): string | null {
  const value = event.dataTransfer.getData(builderDragMimeType) || event.dataTransfer.getData('text/plain');
  return value || null;
}

function isDragBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(target.closest('[data-av-no-drag],button,input,select,textarea,a,[role="button"]'));
}

function getSectionLabel(type: BuilderSectionType, t: Translate): string {
  return t(builderSectionCatalog.find((entry) => entry.type === type)?.labelKey ?? type);
}

function getVariantLabel(variant: BuilderSection['variant'], t: Translate): string {
  return t(`project.builder.variants.${variant}`);
}

function getPreviewDeviceLabel(device: BuilderPreviewDevice, t: Translate): string {
  return t(`project.builder.preview.devices.${device}`);
}

function getValidationBadgeLabel(validation: { errorCount: number; warningCount: number }, t: Translate): string {
  if (validation.errorCount > 0) {
    return t('project.builder.status.errors', { count: validation.errorCount });
  }

  if (validation.warningCount > 0) {
    return t('project.builder.status.warnings', { count: validation.warningCount });
  }

  return t('project.builder.status.valid');
}

function getSectionStateLabel(sectionState: string, section: BuilderSection, t: Translate): string {
  if (sectionState === 'error') {
    return t('project.builder.validation.error');
  }

  if (sectionState === 'warning') {
    return t('project.builder.validation.warning');
  }

  return getVariantLabel(section.variant, t);
}

function getBuilderPageStatusTone(status: BuilderPageStatus): 'warning' | 'success' | 'neutral' {
  if (status === 'draft') {
    return 'warning';
  }

  if (status === 'ready') {
    return 'success';
  }

  return 'neutral';
}

function isRepeaterSection(
  section: BuilderSection | null
): section is Extract<BuilderSection, { type: 'services' | 'features' | 'steps' | 'stats' | 'testimonials' | 'faq' | 'gallery' }> {
  return Boolean(section && ['services', 'features', 'steps', 'stats', 'testimonials', 'faq', 'gallery'].includes(section.type));
}

function createRepeaterItem(section: Extract<BuilderSection, { type: 'services' | 'features' | 'steps' | 'stats' | 'testimonials' | 'faq' | 'gallery' }>): any {
  switch (section.type) {
    case 'services':
      return {
        id: createLocalId('service'),
        title: '',
        description: '',
        icon: { assetId: null, altText: null }
      };
    case 'features':
      return {
        id: createLocalId('feature'),
        title: '',
        description: ''
      };
    case 'steps':
      return {
        id: createLocalId('step'),
        title: '',
        description: ''
      };
    case 'stats':
      return {
        id: createLocalId('stat'),
        value: '',
        label: ''
      };
    case 'testimonials':
      return {
        id: createLocalId('testimonial'),
        quote: '',
        author: '',
        role: ''
      };
    case 'faq':
      return {
        id: createLocalId('faq'),
        question: '',
        answer: ''
      };
    case 'gallery':
      return {
        id: createLocalId('gallery'),
        image: { assetId: null, altText: null },
        caption: ''
      };
  }
}

function renderSectionPreview(
  section: BuilderSection,
  assetsById: Map<string, AssetRecord>,
  isSelected: boolean,
  t: Translate,
  onSelect: () => void
): JSX.Element {
  const commonProps = {
    className: [
      'av-builder-preview-section',
      `av-builder-preview-section--${section.type}`,
      isSelected ? 'is-selected' : '',
      !section.enabled ? 'is-disabled' : ''
    ].filter(Boolean).join(' '),
    onClick: () => onSelect()
  };

  switch (section.type) {
    case 'hero':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          {section.props.eyebrow ? <p className="av-builder-preview-eyebrow">{section.props.eyebrow}</p> : null}
          <h1>{section.props.heading}</h1>
          <p>{section.props.subheading}</p>
          <div className="av-builder-preview-actions">
            {section.props.primaryCta.label ? <span className="av-primary-button">{section.props.primaryCta.label}</span> : null}
            {section.props.secondaryCta.label ? <span className="av-secondary-button">{section.props.secondaryCta.label}</span> : null}
          </div>
          {section.props.media.assetId ? (
            <div className="av-builder-preview-media">{assetsById.get(section.props.media.assetId)?.name ?? t('project.builder.assets.selected')}</div>
          ) : null}
        </section>
      );
    case 'about':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          <h2>{section.props.heading}</h2>
          <p>{section.props.intro}</p>
          <p>{section.props.body}</p>
          <ul>{section.props.highlights.map((item, index) => <li key={`${section.id}-highlight-${index}`}>{item}</li>)}</ul>
        </section>
      );
    case 'services':
    case 'features':
    case 'steps':
    case 'stats':
    case 'testimonials':
    case 'faq':
    case 'gallery':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          <h2>{section.props.heading}</h2>
          <p>{section.props.intro}</p>
          <div className="av-builder-preview-grid">
            {section.props.items.map((item) => (
              <article key={item.id} className="av-builder-preview-card">
                {'value' in item ? <strong>{item.value}</strong> : null}
                {'question' in item ? <strong>{item.question}</strong> : null}
                {'title' in item ? <strong>{item.title}</strong> : null}
                {'quote' in item ? <p>{item.quote}</p> : null}
                {'caption' in item ? <p>{item.caption}</p> : null}
                {'label' in item ? <span>{item.label}</span> : null}
                {'description' in item ? <span>{item.description}</span> : null}
                {'answer' in item ? <span>{item.answer}</span> : null}
                {'role' in item ? <span>{item.author}{item.role ? ` - ${item.role}` : ''}</span> : null}
                {'image' in item && item.image.assetId ? <span>{assetsById.get(item.image.assetId)?.name ?? t('project.builder.assets.selected')}</span> : null}
                {'icon' in item && item.icon.assetId ? <span>{assetsById.get(item.icon.assetId)?.name ?? t('project.builder.assets.icon')}</span> : null}
              </article>
            ))}
          </div>
        </section>
      );
    case 'cta':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          <h2>{section.props.heading}</h2>
          <p>{section.props.body}</p>
          <div className="av-builder-preview-actions">
            {section.props.primaryCta.label ? <span className="av-primary-button">{section.props.primaryCta.label}</span> : null}
            {section.props.secondaryCta.label ? <span className="av-secondary-button">{section.props.secondaryCta.label}</span> : null}
          </div>
        </section>
      );
    case 'contact':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          <h2>{section.props.heading}</h2>
          <p>{section.props.intro}</p>
          <div className="av-builder-preview-meta">
            <span>{section.props.address}</span>
            <span>{section.props.phone}</span>
            <span>{section.props.email}</span>
          </div>
        </section>
      );
    case 'footer':
      return (
        <section {...commonProps}>
          <div className="av-builder-preview-label">
            {getSectionLabel(section.type, t)} / {getVariantLabel(section.variant, t)}
          </div>
          <h2>{section.props.companyName}</h2>
          <p>{section.props.tagline}</p>
          <div className="av-builder-preview-meta">
            <span>{section.props.contactEmail}</span>
            <span>{section.props.contactPhone}</span>
          </div>
          <div className="av-builder-preview-links">
            {section.props.links.map((link) => (
              <span key={link.id}>{link.label}</span>
            ))}
          </div>
        </section>
      );
  }
}

function BuilderField({
  label,
  hint,
  children
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="av-form-field av-builder-field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function BuilderTextArea({
  value,
  rows = 4,
  onChange
}: {
  value: string;
  rows?: number;
  onChange: (value: string) => void;
}): JSX.Element {
  return <TextAreaField className="av-textarea" rows={rows} value={value} onValueChange={onChange} />;
}

function AssetReferenceEditor({
  label,
  value,
  assets,
  t,
  onChange
}: {
  label: string;
  value: BuilderAssetReference;
  assets: AssetRecord[];
  t: Translate;
  onChange: (value: BuilderAssetReference) => void;
}): JSX.Element {
  return (
    <div className="av-builder-field-group">
      <BuilderField label={label}>
        <SelectField
          value={value.assetId ?? ''}
          options={[
            { value: '', label: t('project.builder.assets.none') },
            ...assets.map((asset) => ({
              value: asset.id,
              label: asset.name
            }))
          ]}
          onChange={(event) =>
            onChange({
              ...value,
              assetId: event.target.value ? event.target.value : null
            })
          }
        />
      </BuilderField>
      <BuilderField label={t('project.builder.fields.altText')}>
        <TextField
          value={value.altText ?? ''}
          onValueChange={(nextValue) =>
            onChange({
              ...value,
              altText: nextValue.trim() ? nextValue : null
            })
          }
        />
      </BuilderField>
    </div>
  );
}

function LinkEditor({
  title,
  value,
  t,
  onChange
}: {
  title: string;
  value: BuilderLinkField;
  t: Translate;
  onChange: (value: BuilderLinkField) => void;
}): JSX.Element {
  return (
    <div className="av-builder-field-group">
      <BuilderField label={`${title} - ${t('project.builder.fields.text')}`}>
        <TextField value={value.label} onValueChange={(nextValue) => onChange({ ...value, label: nextValue })} />
      </BuilderField>
      <BuilderField label={`${title} - ${t('project.builder.fields.link')}`}>
        <TextField value={value.href} onValueChange={(nextValue) => onChange({ ...value, href: nextValue })} />
      </BuilderField>
      <BuilderField label={`${title} - ${t('project.builder.fields.style')}`}>
        <SelectField
          value={value.style}
          options={[
            { value: 'primary', label: t('project.builder.buttonStyles.primary') },
            { value: 'secondary', label: t('project.builder.buttonStyles.secondary') }
          ]}
          onChange={(event) => onChange({ ...value, style: event.target.value as BuilderLinkField['style'] })}
        />
      </BuilderField>
    </div>
  );
}

function BuilderDialog({
  title,
  description,
  children,
  actions
}: {
  title: string;
  description?: string;
  children?: React.ReactNode;
  actions: React.ReactNode;
}): JSX.Element {
  return (
    <div className="av-builder-dialog__overlay" role="presentation">
      <div aria-modal="true" className="av-builder-dialog" role="dialog">
        <header className="av-builder-dialog__header">
          <div>
            <strong>{title}</strong>
            {description ? <span>{description}</span> : null}
          </div>
        </header>
        {children ? <div className="av-builder-dialog__body">{children}</div> : null}
        <footer className="av-builder-dialog__actions">{actions}</footer>
      </div>
    </div>
  );
}

function renderValidationIssues(
  issues: Array<{ id: string; level: 'error' | 'warning'; message: string }>,
  t: Translate
): JSX.Element | null {
  if (issues.length === 0) {
    return null;
  }

  return (
    <div className="av-builder-validation">
      {issues.map((issue) => (
        <div key={issue.id} className={['av-builder-validation__item', issue.level === 'error' ? 'is-error' : 'is-warning'].join(' ')}>
          <strong>{issue.level === 'error' ? t('project.builder.validation.error') : t('project.builder.validation.warning')}</strong>
          <span>{issue.message}</span>
        </div>
      ))}
    </div>
  );
}

export function ProjectBuilderWorkspace({
  detail,
  onDetailChange,
  builderFlushRef
}: {
  detail: ProjectDetailRecord;
  onDetailChange: (detail: ProjectDetailRecord) => void;
  builderFlushRef?: MutableRefObject<(() => Promise<void>) | null>;
}): JSX.Element {
  const { t } = useI18n();
  const addToast = useUiStore((state) => state.addToast);
  const [builderTab, setBuilderTab] = useState<BuilderWorkspaceTab>('content');
  const [isUpdatingThemePreset, setIsUpdatingThemePreset] = useState(false);
  const pages = useProjectBuilderStore((state) => state.pages);
  const selectedSectionId = useProjectBuilderStore((state) => state.selectedSectionId);
  const previewDevice = useProjectBuilderStore((state) => state.previewDevice);
  const collapsedSectionIds = useProjectBuilderStore((state) => state.collapsedSectionIds);
  const isPickerOpen = useProjectBuilderStore((state) => state.isPickerOpen);
  const isMaximized = useProjectBuilderStore((state) => state.isMaximized);
  const isSaving = useProjectBuilderStore((state) => state.isSaving);
  const isDirty = useProjectBuilderStore((state) => state.isDirty);
  const draggedSectionId = useProjectBuilderStore((state) => state.draggedSectionId);
  const selectPage = useProjectBuilderStore((state) => state.selectPage);
  const selectSection = useProjectBuilderStore((state) => state.selectSection);
  const setPreviewDevice = useProjectBuilderStore((state) => state.setPreviewDevice);
  const toggleCollapsed = useProjectBuilderStore((state) => state.toggleCollapsed);
  const openPicker = useProjectBuilderStore((state) => state.openPicker);
  const closePicker = useProjectBuilderStore((state) => state.closePicker);
  const toggleMaximized = useProjectBuilderStore((state) => state.toggleMaximized);
  const setDraggedSectionId = useProjectBuilderStore((state) => state.setDraggedSectionId);
  const setPageField = useProjectBuilderStore((state) => state.setPageField);
  const setThemeTone = useProjectBuilderStore((state) => state.setThemeTone);
  const addSection = useProjectBuilderStore((state) => state.addSection);
  const duplicateSection = useProjectBuilderStore((state) => state.duplicateSection);
  const deleteSection = useProjectBuilderStore((state) => state.deleteSection);
  const toggleSectionEnabled = useProjectBuilderStore((state) => state.toggleSectionEnabled);
  const moveSection = useProjectBuilderStore((state) => state.moveSection);
  const reorderSections = useProjectBuilderStore((state) => state.reorderSections);
  const updateSection = useProjectBuilderStore((state) => state.updateSection);
  const {
    activePage,
    canDeletePage,
    createPageDraft,
    flushPendingSaves,
    isCreatePageDialogOpen,
    isDeletePageDialogOpen,
    openCreatePageDialog,
    closeCreatePageDialog,
    updateCreatePageTitle,
    updateCreatePageSlug,
    updateCreatePageType,
    updateCreatePageMenuLocation,
    submitCreatePage,
    openDeletePageDialog,
    closeDeletePageDialog,
    confirmDeletePage,
    setPrimaryPage
  } = useProjectBuilderController({
    detail,
    onDetailChange,
    ...(builderFlushRef !== undefined ? { builderFlushRef } : {})
  });

  const selectedSection = useMemo(
    () => activePage?.document.sections.find((section) => section.id === selectedSectionId) ?? null,
    [activePage, selectedSectionId]
  );
  const assetsById = useMemo(() => new Map(detail.assets.map((asset) => [asset.id, asset] as const)), [detail.assets]);
  const projectLogoAsset = useMemo(
    () => detail.assets.find((asset) => asset.id === detail.project.logoAssetId) ?? null,
    [detail.assets, detail.project.logoAssetId]
  );
  const projectIconAsset = useMemo(
    () => detail.assets.find((asset) => asset.id === detail.project.iconAssetId) ?? null,
    [detail.assets, detail.project.iconAssetId]
  );
  const seoPreviewUrl = useMemo(() => {
    if (!activePage) {
      return null;
    }

    const base = detail.project.primaryDomain ?? detail.project.localDomain ?? null;
    const normalizedBase = base
      ? base.startsWith('http://') || base.startsWith('https://')
        ? base
        : `https://${base}`
      : 'https://website.local';
    const slug = activePage.document.pageMeta.slug.trim().replace(/^\/+/, '');

    if (activePage.isPrimary || slug === '' || slug === 'home' || slug === 'homepage') {
      return normalizedBase;
    }

    return `${normalizedBase.replace(/\/+$/, '')}/${slug}`;
  }, [activePage, detail.project.localDomain, detail.project.primaryDomain]);

  const activePageIssues = activePage?.validation.issues ?? [];
  const pageIssues = activePageIssues.filter((issue) => issue.scope === 'page');
  const selectedSectionIssues = selectedSection ? activePageIssues.filter((issue) => issue.sectionId === selectedSection.id) : [];
  const pageStatusOptions = [
    { value: 'draft', label: t('enums.builderPageStatus.draft') },
    { value: 'ready', label: t('enums.builderPageStatus.ready') },
    { value: 'archived', label: t('enums.builderPageStatus.archived') }
  ];
  const pageTypeOptions = builderPageTypeCatalog.map((entry) => ({
    value: entry.value,
    label: t(entry.labelKey)
  }));
  const menuLocationOptions = [
    { value: 'none', label: t('project.builder.menuLocations.none') },
    { value: 'primary', label: t('project.builder.menuLocations.primary') },
    { value: 'footer', label: t('project.builder.menuLocations.footer') }
  ];
  const toneOptions = [
    { value: 'default', label: t('project.builder.tones.default') },
    { value: 'brand', label: t('project.builder.tones.brand') },
    { value: 'muted', label: t('project.builder.tones.muted') },
    { value: 'contrast', label: t('project.builder.tones.contrast') }
  ];
  const currentThemePreset =
    builderThemePresetCatalog.find((preset) => preset.id === detail.project.builderThemePresetId) ??
    builderThemePresetCatalog.find((preset) => preset.id === 'corporate_services') ??
    builderThemePresetCatalog[0]!;

  function fieldLabel(key: string): string {
    return t(`project.builder.fields.${key}`);
  }

  function updateSelectedSectionProps(updater: (props: any) => any): void {
    if (!selectedSection) {
      return;
    }

    updateSection(selectedSection.id, (section: any) => ({
      ...section,
      props: updater(section.props)
    }));
  }

  function updateSelectedItem(itemId: string, updater: (item: any) => any): void {
    if (!selectedSection || !isRepeaterSection(selectedSection)) {
      return;
    }

    updateSection(selectedSection.id, (section: any) => ({
      ...section,
      props: {
        ...section.props,
        items: section.props.items.map((entry: any) => (entry.id === itemId ? updater(entry) : entry))
      }
    }));
  }

  function removeSelectedItem(itemId: string): void {
    if (!selectedSection || !isRepeaterSection(selectedSection)) {
      return;
    }

    updateSection(selectedSection.id, (section: any) => ({
      ...section,
      props: {
        ...section.props,
        items: section.props.items.filter((entry: any) => entry.id !== itemId)
      }
    }));
  }

  function appendSelectedItem(): void {
    if (!selectedSection || !isRepeaterSection(selectedSection)) {
      return;
    }

    updateSection(selectedSection.id, (section: any) => ({
      ...section,
      props: {
        ...section.props,
        items: [...section.props.items, createRepeaterItem(section)]
      }
    }));
  }

  function addAboutHighlight(): void {
    if (!selectedSection || selectedSection.type !== 'about') {
      return;
    }

    updateSection(selectedSection.id, (section: AboutSection) => ({
      ...section,
      props: {
        ...section.props,
        highlights: [...section.props.highlights, t('project.builder.defaults.highlight')]
      }
    }));
  }

  function addFooterLink(): void {
    if (!selectedSection || selectedSection.type !== 'footer') {
      return;
    }

    updateSelectedSectionProps((props) => ({
      ...props,
      links: [...props.links, { id: createLocalId('footer-link'), label: '', href: '' }]
    }));
  }

  async function handleThemePresetChange(nextPresetId: BuilderThemePresetId): Promise<void> {
    if (detail.project.builderThemePresetId === nextPresetId || isUpdatingThemePreset) {
      return;
    }

    setIsUpdatingThemePreset(true);

    try {
      await flushPendingSaves();
      const nextDetail = await updateProject({
        id: detail.project.id,
        builderThemePresetId: nextPresetId
      });
      onDetailChange(nextDetail);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('project.builder.theme.updateFailed');
      addToast({
        title: t('project.builder.toast.title'),
        message,
        tone: 'danger'
      });
    } finally {
      setIsUpdatingThemePreset(false);
    }
  }

  function handleBuilderTabChange(nextTab: BuilderWorkspaceTab): void {
    if (nextTab !== 'content') {
      closePicker();
    }

    setBuilderTab(nextTab);
  }

  if (pages.length === 0) {
    return (
      <section className="av-builder-workspace av-builder-workspace--empty">
        <EmptyState title={t('project.builder.empty.title')} description={t('project.builder.empty.description')} />
        <div className="av-builder-empty-actions">
          <button className="av-primary-button" type="button" onClick={openCreatePageDialog}>
            <Icon name="plus" />
            <span>{t('project.builder.empty.createFirstPage')}</span>
          </button>
        </div>

        {isCreatePageDialogOpen ? (
          <BuilderDialog
            title={t('project.builder.dialogs.create.title')}
            description={t('project.builder.dialogs.create.description')}
            actions={
              <>
                <button className="av-secondary-button" type="button" onClick={closeCreatePageDialog}>
                  {t('common.cancel')}
                </button>
                <button className="av-primary-button" type="button" onClick={() => void submitCreatePage()}>
                  {t('project.builder.dialogs.create.submit')}
                </button>
              </>
            }
          >
            <div className="av-builder-dialog__grid">
              <BuilderField label={fieldLabel('pageTitle')}>
                <TextField value={createPageDraft.title} onValueChange={updateCreatePageTitle} />
              </BuilderField>
              <BuilderField label={fieldLabel('slug')}>
                <TextField value={createPageDraft.slug} onValueChange={updateCreatePageSlug} />
              </BuilderField>
              <BuilderField label={fieldLabel('pageType')}>
                <SelectField value={createPageDraft.pageType} options={pageTypeOptions} onChange={(event) => updateCreatePageType(event.target.value as BuilderPageType)} />
              </BuilderField>
              <BuilderField label={fieldLabel('menuLocation')}>
                <SelectField
                  value={createPageDraft.menuLocation}
                  options={menuLocationOptions}
                  onChange={(event) => updateCreatePageMenuLocation(event.target.value as BuilderMenuLocation)}
                />
              </BuilderField>
            </div>
          </BuilderDialog>
        ) : null}
      </section>
    );
  }

  return (
    <section className={['av-builder-workspace', isMaximized ? 'is-maximized' : ''].filter(Boolean).join(' ')}>
      <header className="av-builder-toolbar">
        <div className="av-builder-toolbar__group">
          <SelectField
            value={activePage?.id ?? ''}
            options={pages.map((page) => ({
              value: page.id,
              label: page.isPrimary
                ? `${page.document.pageMeta.title} - ${t('project.builder.page.primarySuffix')}`
                : page.document.pageMeta.title
            }))}
            onChange={(event) => selectPage(event.target.value)}
          />
          <button className="av-secondary-button" type="button" onClick={openCreatePageDialog}>
            <Icon name="plus" />
            <span>{t('project.builder.page.newPage')}</span>
          </button>
        </div>

        <div className="av-builder-toolbar__group">
          {builderTab === 'content'
            ? (['desktop', 'tablet', 'mobile'] as BuilderPreviewDevice[]).map((device) => (
                <button
                  key={device}
                  className={['av-filter-chip', previewDevice === device ? 'is-active' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => setPreviewDevice(device)}
                >
                  <span>{getPreviewDeviceLabel(device, t)}</span>
                </button>
              ))
            : null}
          <div className="av-builder-toolbar__status">
            <StatusBadge
              label={activePage ? getValidationBadgeLabel(activePage.validation, t) : t('project.builder.status.valid')}
              tone={activePage?.validation.errorCount ? 'warning' : activePage?.validation.warningCount ? 'info' : 'success'}
            />
            <span>
              {isSaving
                ? t('project.builder.status.saving')
                : isDirty
                  ? t('project.builder.status.unsaved')
                  : t('project.builder.status.saved')}
            </span>
          </div>
          <IconButton
            icon={isMaximized ? 'minimize' : 'maximize'}
            label={isMaximized ? t('project.builder.actions.restoreWorkspace') : t('project.builder.actions.maximizeWorkspace')}
            active={isMaximized}
            onClick={toggleMaximized}
            title={isMaximized ? t('project.builder.actions.restoreWorkspace') : t('project.builder.actions.maximizeWorkspace')}
          />
        </div>
      </header>

      <div className="av-builder-tabs">
        <EditorTabs
          tabs={[
            { id: 'content', label: t('project.builder.tabs.content'), icon: 'builder' },
            { id: 'pages', label: t('project.builder.tabs.pages'), icon: 'projects' },
            { id: 'seo', label: t('project.builder.tabs.seo'), icon: 'search' },
            { id: 'theme', label: t('project.builder.tabs.theme'), icon: 'star' }
          ]}
          value={builderTab}
          onChange={handleBuilderTabChange}
        />
      </div>

      {builderTab === 'content' ? (
        <div className="av-builder-layout">
          <aside className="av-builder-panel av-builder-structure">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.structure.title')}</strong>
                <span>{t('project.builder.structure.sectionCount', { count: activePage?.document.sections.length ?? 0 })}</span>
              </div>
              <button className="av-secondary-button" type="button" onClick={openPicker}>
                <Icon name="plus" />
                <span>{t('project.builder.structure.addSection')}</span>
              </button>
            </div>

            {activePage?.document.sections.length ? (
              <div className="av-builder-structure__list">
                {activePage.document.sections.map((section, index) => {
                  const sectionState = activePage.validation.sectionStates[section.id] ?? 'unknown';

                  return (
                    <article
                      key={section.id}
                      className={[
                        'av-builder-structure-item',
                        selectedSectionId === section.id ? 'is-selected' : '',
                        draggedSectionId === section.id ? 'is-dragging' : ''
                      ].filter(Boolean).join(' ')}
                      draggable
                      onClick={() => selectSection(section.id)}
                      onDragStart={(event) => {
                        if (isDragBlockedTarget(event.target)) {
                          event.preventDefault();
                          return;
                        }

                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData(builderDragMimeType, section.id);
                        event.dataTransfer.setData('text/plain', section.id);
                        setDraggedSectionId(section.id);
                      }}
                      onDragEnd={() => setDraggedSectionId(null)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const dragSectionId = readDraggedSectionId(event);
                        if (dragSectionId) {
                          reorderSections(dragSectionId, section.id);
                        }
                      }}
                    >
                      <div className="av-builder-structure-item__top">
                        <button
                          className="av-builder-structure-item__collapse"
                          type="button"
                          data-av-no-drag
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleCollapsed(section.id);
                          }}
                        >
                          {collapsedSectionIds.includes(section.id) ? '+' : '-'}
                        </button>
                        <div className="av-builder-structure-item__copy">
                          <strong>{getSectionLabel(section.type, t)}</strong>
                          <span>{summarizeBuilderSection(section)}</span>
                        </div>
                        <StatusBadge
                          label={getSectionStateLabel(sectionState, section, t)}
                          tone={sectionState === 'error' ? 'warning' : sectionState === 'warning' ? 'info' : 'neutral'}
                        />
                      </div>

                      {!collapsedSectionIds.includes(section.id) ? (
                        <div className="av-builder-structure-item__actions" data-av-no-drag>
                          <IconButton
                            className="av-builder-icon-action"
                            disabled={index === 0}
                            icon="arrowUp"
                            label={t('project.builder.actions.moveUp')}
                            onClick={() => moveSection(section.id, -1)}
                            title={t('project.builder.actions.moveUp')}
                          />
                          <IconButton
                            className="av-builder-icon-action"
                            disabled={index === activePage.document.sections.length - 1}
                            icon="arrowDown"
                            label={t('project.builder.actions.moveDown')}
                            onClick={() => moveSection(section.id, 1)}
                            title={t('project.builder.actions.moveDown')}
                          />
                          <IconButton
                            className="av-builder-icon-action"
                            icon="duplicate"
                            label={t('project.builder.actions.duplicate')}
                            onClick={() => duplicateSection(section.id)}
                            title={t('project.builder.actions.duplicate')}
                          />
                          <IconButton
                            className="av-builder-icon-action"
                            icon={section.enabled ? 'eyeOff' : 'eye'}
                            label={section.enabled ? t('project.builder.actions.hide') : t('project.builder.actions.show')}
                            onClick={() => toggleSectionEnabled(section.id)}
                            title={section.enabled ? t('project.builder.actions.hide') : t('project.builder.actions.show')}
                          />
                          <IconButton
                            className="av-builder-icon-action"
                            icon="trash"
                            label={t('project.builder.actions.deleteSection')}
                            onClick={() => deleteSection(section.id)}
                            title={t('project.builder.actions.deleteSection')}
                          />
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState title={t('project.builder.structure.emptyTitle')} description={t('project.builder.structure.emptyDescription')} />
            )}
          </aside>

          <main className="av-builder-panel av-builder-preview-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.preview.title')}</strong>
                <span>{activePage?.document.pageMeta.title}</span>
              </div>
              <span className="av-builder-preview-device">{getPreviewDeviceLabel(previewDevice, t)}</span>
            </div>

            <div
              className={['av-builder-preview-shell', `is-${previewDevice}`].join(' ')}
              data-av-builder-preset={currentThemePreset.id}
              data-av-builder-hero={currentThemePreset.components.hero}
              data-av-builder-card={currentThemePreset.components.card}
              data-av-builder-stats={currentThemePreset.components.stats}
              data-av-builder-cta={currentThemePreset.components.cta}
              style={getBuilderThemeCssVariables(currentThemePreset.tokens) as CSSProperties}
            >
              <div className="av-builder-preview-page">
                {activePage ? (
                  activePage.document.sections.map((section) => (
                    <Fragment key={section.id}>
                      {renderSectionPreview(section, assetsById, selectedSectionId === section.id, t, () => selectSection(section.id))}
                    </Fragment>
                  ))
                ) : (
                  <EmptyState title={t('project.builder.preview.emptyTitle')} description={t('project.builder.preview.emptyDescription')} />
                )}
              </div>
            </div>
          </main>

          <aside className="av-builder-panel av-builder-inspector">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.inspector.sectionTitle')}</strong>
                <span>{selectedSection ? getSectionLabel(selectedSection.type, t) : t('project.builder.inspector.emptyTitle')}</span>
              </div>
            </div>

            <div className="av-builder-inspector__body">
              {!selectedSection ? (
                <div className="av-builder-empty-panel">
                  <strong>{t('project.builder.inspector.emptyTitle')}</strong>
                  <span>{t('project.builder.inspector.emptyDescription')}</span>
                </div>
              ) : null}

              {selectedSection?.type === 'hero' ? (
                <>
                  <BuilderField label={fieldLabel('eyebrow')}>
                    <TextField value={selectedSection.props.eyebrow} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, eyebrow: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('heading')}>
                    <BuilderTextArea value={selectedSection.props.heading} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, heading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('subheading')}>
                    <BuilderTextArea value={selectedSection.props.subheading} rows={4} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, subheading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('alignment')}>
                    <SelectField
                      value={selectedSection.props.alignment}
                      options={[
                        { value: 'start', label: t('project.builder.alignments.start') },
                        { value: 'center', label: t('project.builder.alignments.center') },
                        { value: 'end', label: t('project.builder.alignments.end') }
                      ]}
                      onChange={(event) => updateSelectedSectionProps((props) => ({ ...props, alignment: event.target.value }))}
                    />
                  </BuilderField>
                  <LinkEditor
                    title={fieldLabel('primaryCta')}
                    value={selectedSection.props.primaryCta}
                    t={t}
                    onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, primaryCta: value }))}
                  />
                  <LinkEditor
                    title={fieldLabel('secondaryCta')}
                    value={selectedSection.props.secondaryCta}
                    t={t}
                    onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, secondaryCta: value }))}
                  />
                  <AssetReferenceEditor
                    label={fieldLabel('media')}
                    value={selectedSection.props.media}
                    assets={detail.assets}
                    t={t}
                    onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, media: value }))}
                  />
                </>
              ) : null}

              {selectedSection?.type === 'about' ? (
                <>
                  <BuilderField label={fieldLabel('heading')}>
                    <TextField value={selectedSection.props.heading} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, heading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('intro')}>
                    <BuilderTextArea value={selectedSection.props.intro} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, intro: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('body')}>
                    <BuilderTextArea value={selectedSection.props.body} rows={5} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, body: value }))} />
                  </BuilderField>
                  <div className="av-builder-list-editor">
                    {selectedSection.props.highlights.map((item: string, index: number) => (
                      <div key={`${selectedSection.id}-${index}`} className="av-builder-list-editor__row">
                        <TextField
                          value={item}
                          onValueChange={(value) =>
                            updateSelectedSectionProps((props) => ({
                              ...props,
                              highlights: props.highlights.map((entry: string, entryIndex: number) => (entryIndex === index ? value : entry))
                            }))
                          }
                        />
                        <IconButton
                          className="av-builder-icon-action"
                          icon="trash"
                          label={t('project.builder.actions.deleteHighlight')}
                          onClick={() =>
                            updateSelectedSectionProps((props) => ({
                              ...props,
                              highlights: props.highlights.filter((_: string, entryIndex: number) => entryIndex !== index)
                            }))
                          }
                          title={t('project.builder.actions.deleteHighlight')}
                        />
                      </div>
                    ))}
                    <button className="av-secondary-button" type="button" onClick={addAboutHighlight}>
                      {t('project.builder.actions.addHighlight')}
                    </button>
                  </div>
                </>
              ) : null}

              {isRepeaterSection(selectedSection) ? (
                <>
                  <BuilderField label={fieldLabel('heading')}>
                    <TextField value={selectedSection.props.heading} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, heading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('intro')}>
                    <BuilderTextArea value={selectedSection.props.intro} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, intro: value }))} />
                  </BuilderField>
                  <div className="av-builder-repeater">
                    {selectedSection.props.items.map((item: any) => (
                      <div key={item.id} className="av-builder-repeater__item">
                        <div className="av-builder-repeater__item-actions">
                          <IconButton
                            className="av-builder-icon-action"
                            icon="trash"
                            label={t('project.builder.actions.deleteItem')}
                            onClick={() => removeSelectedItem(item.id)}
                            title={t('project.builder.actions.deleteItem')}
                          />
                        </div>
                        {'title' in item ? (
                          <BuilderField label={fieldLabel('title')}>
                            <TextField value={item.title} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, title: value }))} />
                          </BuilderField>
                        ) : null}
                        {'question' in item ? (
                          <BuilderField label={fieldLabel('question')}>
                            <TextField value={item.question} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, question: value }))} />
                          </BuilderField>
                        ) : null}
                        {'value' in item ? (
                          <BuilderField label={fieldLabel('value')}>
                            <TextField value={item.value} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, value }))} />
                          </BuilderField>
                        ) : null}
                        {'quote' in item ? (
                          <BuilderField label={fieldLabel('quote')}>
                            <BuilderTextArea value={item.quote} rows={3} onChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, quote: value }))} />
                          </BuilderField>
                        ) : null}
                        {'caption' in item ? (
                          <BuilderField label={fieldLabel('caption')}>
                            <TextField value={item.caption} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, caption: value }))} />
                          </BuilderField>
                        ) : null}
                        {'label' in item ? (
                          <BuilderField label={fieldLabel('label')}>
                            <TextField value={item.label} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, label: value }))} />
                          </BuilderField>
                        ) : null}
                        {'author' in item ? (
                          <BuilderField label={fieldLabel('author')}>
                            <TextField value={item.author} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, author: value }))} />
                          </BuilderField>
                        ) : null}
                        {'role' in item ? (
                          <BuilderField label={fieldLabel('role')}>
                            <TextField value={item.role} onValueChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, role: value }))} />
                          </BuilderField>
                        ) : null}
                        {'description' in item ? (
                          <BuilderField label={fieldLabel('description')}>
                            <BuilderTextArea value={item.description} rows={3} onChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, description: value }))} />
                          </BuilderField>
                        ) : null}
                        {'answer' in item ? (
                          <BuilderField label={fieldLabel('answer')}>
                            <BuilderTextArea value={item.answer} rows={3} onChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, answer: value }))} />
                          </BuilderField>
                        ) : null}
                        {'image' in item ? (
                          <AssetReferenceEditor
                            label={fieldLabel('image')}
                            value={item.image}
                            assets={detail.assets}
                            t={t}
                            onChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, image: value }))}
                          />
                        ) : null}
                        {'icon' in item ? (
                          <AssetReferenceEditor
                            label={fieldLabel('icon')}
                            value={item.icon}
                            assets={detail.assets}
                            t={t}
                            onChange={(value) => updateSelectedItem(item.id, (entry) => ({ ...entry, icon: value }))}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <button className="av-secondary-button" type="button" onClick={appendSelectedItem}>
                    {t('project.builder.actions.addItem')}
                  </button>
                </>
              ) : null}

              {selectedSection?.type === 'cta' ? (
                <>
                  <BuilderField label={fieldLabel('heading')}>
                    <TextField value={selectedSection.props.heading} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, heading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('body')}>
                    <BuilderTextArea value={selectedSection.props.body} rows={4} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, body: value }))} />
                  </BuilderField>
                  <LinkEditor
                    title={fieldLabel('primaryCta')}
                    value={selectedSection.props.primaryCta}
                    t={t}
                    onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, primaryCta: value }))}
                  />
                  <LinkEditor
                    title={fieldLabel('secondaryCta')}
                    value={selectedSection.props.secondaryCta}
                    t={t}
                    onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, secondaryCta: value }))}
                  />
                </>
              ) : null}

              {selectedSection?.type === 'contact' ? (
                <>
                  <BuilderField label={fieldLabel('heading')}>
                    <TextField value={selectedSection.props.heading} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, heading: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('intro')}>
                    <BuilderTextArea value={selectedSection.props.intro} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, intro: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('address')}>
                    <BuilderTextArea value={selectedSection.props.address} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, address: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('phone')}>
                    <TextField value={selectedSection.props.phone} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, phone: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('email')}>
                    <TextField value={selectedSection.props.email} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, email: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('mapLabel')} hint={t('project.builder.contact.staticHint')}>
                    <TextField value={selectedSection.props.mapLabel} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, mapLabel: value }))} />
                  </BuilderField>
                </>
              ) : null}

              {selectedSection?.type === 'footer' ? (
                <>
                  <BuilderField label={fieldLabel('companyName')}>
                    <TextField value={selectedSection.props.companyName} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, companyName: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('tagline')}>
                    <BuilderTextArea value={selectedSection.props.tagline} rows={3} onChange={(value) => updateSelectedSectionProps((props) => ({ ...props, tagline: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('email')}>
                    <TextField value={selectedSection.props.contactEmail} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, contactEmail: value }))} />
                  </BuilderField>
                  <BuilderField label={fieldLabel('phone')}>
                    <TextField value={selectedSection.props.contactPhone} onValueChange={(value) => updateSelectedSectionProps((props) => ({ ...props, contactPhone: value }))} />
                  </BuilderField>
                  <div className="av-builder-repeater">
                    {selectedSection.props.links.map((link) => (
                      <div key={link.id} className="av-builder-repeater__item">
                        <div className="av-builder-repeater__item-actions">
                          <IconButton
                            className="av-builder-icon-action"
                            icon="trash"
                            label={t('project.builder.actions.deleteLink')}
                            onClick={() =>
                              updateSelectedSectionProps((props) => ({
                                ...props,
                                links: props.links.filter((entry: any) => entry.id !== link.id)
                              }))
                            }
                            title={t('project.builder.actions.deleteLink')}
                          />
                        </div>
                        <BuilderField label={fieldLabel('label')}>
                          <TextField
                            value={link.label}
                            onValueChange={(value) =>
                              updateSelectedSectionProps((props) => ({
                                ...props,
                                links: props.links.map((entry: any) => (entry.id === link.id ? { ...entry, label: value } : entry))
                              }))
                            }
                          />
                        </BuilderField>
                        <BuilderField label={fieldLabel('link')}>
                          <TextField
                            value={link.href}
                            onValueChange={(value) =>
                              updateSelectedSectionProps((props) => ({
                                ...props,
                                links: props.links.map((entry: any) => (entry.id === link.id ? { ...entry, href: value } : entry))
                              }))
                            }
                          />
                        </BuilderField>
                      </div>
                    ))}
                  </div>
                  <button className="av-secondary-button" type="button" onClick={addFooterLink}>
                    {t('project.builder.actions.addLink')}
                  </button>
                </>
              ) : null}

              {renderValidationIssues(selectedSectionIssues, t)}
            </div>
          </aside>
        </div>
      ) : null}

      {builderTab === 'pages' ? (
        <div className="av-builder-secondary-layout">
          <aside className="av-builder-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.pages.title')}</strong>
                <span>{t('project.builder.pages.count', { count: pages.length })}</span>
              </div>
              <button className="av-secondary-button" type="button" onClick={openCreatePageDialog}>
                <Icon name="plus" />
                <span>{t('project.builder.page.newPage')}</span>
              </button>
            </div>

            <div className="av-builder-pages-list">
              {pages.map((page) => (
                <button
                  key={page.id}
                  aria-pressed={page.id === activePage?.id}
                  className={['av-builder-page-card', page.id === activePage?.id ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={() => selectPage(page.id)}
                >
                  <div className="av-builder-page-card__copy">
                    <strong>{page.document.pageMeta.title}</strong>
                    <span>/{page.document.pageMeta.slug}</span>
                  </div>
                  <div className="av-builder-page-card__meta">
                    {page.isPrimary ? <StatusBadge label={t('project.builder.page.primaryBadge')} tone="success" /> : null}
                    <StatusBadge
                      label={t(`enums.builderPageStatus.${page.document.pageMeta.status}`)}
                      tone={getBuilderPageStatusTone(page.document.pageMeta.status)}
                    />
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <section className="av-builder-panel av-builder-settings-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.pages.settingsTitle')}</strong>
                <span>{activePage?.document.pageMeta.title}</span>
              </div>
              <div className="av-builder-panel__actions">
                {activePage && !activePage.isPrimary ? (
                  <button className="av-secondary-button" type="button" onClick={() => void setPrimaryPage()}>
                    <Icon name="star" />
                    <span>{t('project.builder.page.setPrimary')}</span>
                  </button>
                ) : null}
                <IconButton
                  className="av-builder-icon-action"
                  disabled={!canDeletePage}
                  icon="trash"
                  label={t('project.builder.page.delete')}
                  onClick={openDeletePageDialog}
                  title={t('project.builder.page.delete')}
                />
              </div>
            </div>

            {activePage ? (
              <div className="av-builder-inspector__body">
                <BuilderField label={fieldLabel('pageTitle')}>
                  <TextField value={activePage.document.pageMeta.title} onValueChange={(value) => setPageField('title', value)} />
                </BuilderField>
                <BuilderField label={fieldLabel('slug')}>
                  <TextField value={activePage.document.pageMeta.slug} onValueChange={(value) => setPageField('slug', value)} />
                </BuilderField>
                <BuilderField label={fieldLabel('pageType')}>
                  <SelectField value={activePage.document.pageMeta.pageType} options={pageTypeOptions} onChange={(event) => setPageField('pageType', event.target.value)} />
                </BuilderField>
                <BuilderField label={fieldLabel('status')}>
                  <SelectField value={activePage.document.pageMeta.status} options={pageStatusOptions} onChange={(event) => setPageField('status', event.target.value)} />
                </BuilderField>
                <BuilderField label={fieldLabel('menuLocation')}>
                  <SelectField
                    value={activePage.document.pageMeta.menuLocation}
                    options={menuLocationOptions}
                    onChange={(event) => setPageField('menuLocation', event.target.value)}
                  />
                </BuilderField>

                <div className="av-builder-page-summary-grid">
                  <div className="av-builder-page-summary-card">
                    <span>{t('project.builder.pages.primaryStatus')}</span>
                    <strong>{activePage.isPrimary ? t('project.builder.page.primaryBadge') : t('project.builder.pages.secondaryPage')}</strong>
                  </div>
                  <div className="av-builder-page-summary-card">
                    <span>{t('project.builder.pages.sectionCountLabel')}</span>
                    <strong>{activePage.document.sections.length}</strong>
                  </div>
                </div>

                {renderValidationIssues(pageIssues, t)}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}

      {builderTab === 'seo' ? (
        <div className="av-builder-secondary-layout av-builder-secondary-layout--seo">
          <section className="av-builder-panel av-builder-settings-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.seo.title')}</strong>
                <span>{activePage?.document.pageMeta.title}</span>
              </div>
            </div>

            {activePage ? (
              <div className="av-builder-inspector__body">
                <BuilderField label={fieldLabel('seoTitle')}>
                  <TextField value={activePage.document.pageMeta.seoTitle} onValueChange={(value) => setPageField('seoTitle', value)} />
                </BuilderField>
                <BuilderField label={fieldLabel('seoDescription')}>
                  <BuilderTextArea value={activePage.document.pageMeta.seoDescription} rows={5} onChange={(value) => setPageField('seoDescription', value)} />
                </BuilderField>
                {renderValidationIssues(pageIssues, t)}
              </div>
            ) : null}
          </section>

          <aside className="av-builder-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.seo.previewTitle')}</strong>
                <span>{t('project.builder.seo.previewDescription')}</span>
              </div>
            </div>

            {activePage ? (
              <div className="av-builder-seo-preview">
                <span className="av-builder-seo-preview__url">{seoPreviewUrl}</span>
                <strong>{activePage.document.pageMeta.seoTitle || activePage.document.pageMeta.title}</strong>
                <p>{activePage.document.pageMeta.seoDescription || t('project.builder.seo.previewFallback')}</p>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      {builderTab === 'theme' ? (
        <div className="av-builder-theme-layout">
          <section className="av-builder-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.theme.title')}</strong>
                <span>{t('project.builder.theme.description')}</span>
              </div>
            </div>

            <div className="av-builder-theme-carousel">
              {builderThemePresetCatalog.map((preset) => (
                <button
                  key={preset.id}
                  className={['av-builder-theme-card', preset.id === detail.project.builderThemePresetId ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  type="button"
                  disabled={isUpdatingThemePreset}
                  onClick={() => void handleThemePresetChange(preset.id)}
                >
                  <div className="av-builder-theme-card__preview" style={{ backgroundColor: preset.preview.surfaceColor }}>
                    <div className="av-builder-theme-card__frame">
                      <div className="av-builder-theme-card__hero" style={{ backgroundColor: preset.preview.accentColor }} />
                      <div className="av-builder-theme-card__line" />
                      <div className="av-builder-theme-card__line is-short" />
                      <div className="av-builder-theme-card__grid">
                        <span style={{ backgroundColor: preset.preview.accentColor }} />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                  <div className="av-builder-theme-card__copy">
                    <strong>{t(preset.labelKey)}</strong>
                    <span>{t(preset.descriptionKey)}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <aside className="av-builder-panel av-builder-settings-panel">
            <div className="av-builder-panel__header">
              <div>
                <strong>{t('project.builder.theme.currentTitle')}</strong>
                <span>{t(currentThemePreset.labelKey)}</span>
              </div>
            </div>

            <div className="av-builder-inspector__body">
              {activePage ? (
                <BuilderField label={fieldLabel('tone')}>
                  <SelectField
                    value={activePage.document.themeRefs.tone}
                    options={toneOptions}
                    onChange={(event) => setThemeTone(event.target.value as typeof activePage.document.themeRefs.tone)}
                  />
                </BuilderField>
              ) : null}

              <div className="av-builder-theme-summary">
                <p>{t('project.builder.theme.placeholder')}</p>
              </div>

              <div className="av-builder-branding-grid">
                <div className="av-builder-branding-card">
                  <span>{t('project.fields.logo')}</span>
                  <div className="av-builder-branding-card__preview">
                    {projectLogoAsset ? <img src={getAssetPreviewUrl(projectLogoAsset.id) ?? ''} alt="" /> : <Icon name="images" />}
                  </div>
                  <strong>{projectLogoAsset?.name ?? t('project.builder.theme.noBrandingAsset')}</strong>
                </div>
                <div className="av-builder-branding-card">
                  <span>{t('project.fields.icon')}</span>
                  <div className="av-builder-branding-card__preview">
                    {projectIconAsset ? <img src={getAssetPreviewUrl(projectIconAsset.id) ?? ''} alt="" /> : <Icon name="icon" />}
                  </div>
                  <strong>{projectIconAsset?.name ?? t('project.builder.theme.noBrandingAsset')}</strong>
                </div>
              </div>
            </div>
          </aside>
        </div>
      ) : null}

      {isPickerOpen ? (
        <div className="av-builder-picker">
          <div className="av-builder-picker__header">
            <strong>{t('project.builder.picker.title')}</strong>
            <button className="av-secondary-button" type="button" onClick={closePicker}>
              {t('project.builder.picker.close')}
            </button>
          </div>
          <div className="av-builder-picker__grid">
            {builderSectionCatalog.map((entry) => (
              <button key={entry.type} className="av-builder-picker__item" type="button" onClick={() => addSection(entry.type)}>
                <strong>{t(entry.labelKey)}</strong>
                <span>{t(entry.descriptionKey)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {isCreatePageDialogOpen ? (
        <BuilderDialog
          title={t('project.builder.dialogs.create.title')}
          description={t('project.builder.dialogs.create.description')}
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={closeCreatePageDialog}>
                {t('common.cancel')}
              </button>
              <button className="av-primary-button" type="button" onClick={() => void submitCreatePage()}>
                {t('project.builder.dialogs.create.submit')}
              </button>
            </>
          }
        >
          <div className="av-builder-dialog__grid">
            <BuilderField label={fieldLabel('pageTitle')}>
              <TextField value={createPageDraft.title} onValueChange={updateCreatePageTitle} />
            </BuilderField>
            <BuilderField label={fieldLabel('slug')}>
              <TextField value={createPageDraft.slug} onValueChange={updateCreatePageSlug} />
            </BuilderField>
            <BuilderField label={fieldLabel('pageType')}>
              <SelectField value={createPageDraft.pageType} options={pageTypeOptions} onChange={(event) => updateCreatePageType(event.target.value as BuilderPageType)} />
            </BuilderField>
            <BuilderField label={fieldLabel('menuLocation')}>
              <SelectField
                value={createPageDraft.menuLocation}
                options={menuLocationOptions}
                onChange={(event) => updateCreatePageMenuLocation(event.target.value as BuilderMenuLocation)}
              />
            </BuilderField>
          </div>
        </BuilderDialog>
      ) : null}

      {isDeletePageDialogOpen && activePage ? (
        <BuilderDialog
          title={t('project.builder.dialogs.delete.title')}
          description={t('project.builder.dialogs.delete.description', { title: activePage.document.pageMeta.title })}
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={closeDeletePageDialog}>
                {t('common.cancel')}
              </button>
              <button className="av-primary-button" type="button" onClick={() => void confirmDeletePage()}>
                {t('project.builder.dialogs.delete.submit')}
              </button>
            </>
          }
        />
      ) : null}

      <ProjectBuilderAiBubble
        projectId={detail.project.id}
        themePresetId={detail.project.builderThemePresetId}
      />
    </section>
  );
}
